/**
 * Media Uploader Component — Redesigned
 * Multi-select drag-and-drop with image grid preview and tagging
 */

import React, { useState, useCallback, useRef } from 'react';
import { ImagePlus, Upload, X, FileImage, FileVideo, File, Plus } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { uploadMediaItem, getMediaType, formatFileSize, getFileIcon } from '../../utils/mediaLibrary';
import { MediaItem } from '../../types';
import './MediaUploader.css';

interface MediaUploaderProps {
  onUploadComplete?: (item: MediaItem) => void;
  onCancel?: () => void;
  acceptedTypes?: string;
  maxFileSize?: number;
  allowMultiple?: boolean;
  showTagging?: boolean;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
  preview?: string;
  mediaItem?: MediaItem;
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUploadComplete,
  onCancel,
  acceptedTypes = 'image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.sat,.step,.stp,.iges,.igs,.stl,.obj,.html,.htm,text/html',
  maxFileSize = 100 * 1024 * 1024,
  allowMultiple = true,
  showTagging = true,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();

  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [description, setDescription] = useState('');
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [showTaggingPanel, setShowTaggingPanel] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const processFiles = useCallback((newFiles: File[]) => {
    const processedFiles: UploadingFile[] = newFiles.map((file) => {
      if (file.size > maxFileSize) {
        return {
          file,
          progress: 0,
          status: 'error' as const,
          error: `Too large (max ${formatFileSize(maxFileSize)})`,
        };
      }
      let preview: string | undefined;
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        preview = URL.createObjectURL(file);
      }
      return {
        file,
        progress: 0,
        status: 'pending' as const,
        preview,
      };
    });

    if (allowMultiple) {
      setFiles((prev) => [...prev, ...processedFiles]);
    } else {
      setFiles(processedFiles.slice(0, 1));
    }
    if (processedFiles.length > 0 && showTagging) setShowTaggingPanel(true);
  }, [allowMultiple, maxFileSize, showTagging]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  }, [processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    processFiles(selectedFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [processFiles]);

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const file = prev[index];
      if (file.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (!user?.id || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const uploadFile = files[i];
      if (uploadFile.status === 'complete' || uploadFile.status === 'error') continue;

      setFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading' } : f)));

      try {
        const mediaItem = await uploadMediaItem(uploadFile.file, {
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
          tags,
          userTags,
          labels,
          description,
          onProgress: (progress) => {
            setFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, progress } : f)));
          },
        });

        setFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'complete', progress: 100, mediaItem } : f))
        );
        onUploadComplete?.(mediaItem);
      } catch (error) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: 'error', error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          )
        );
      }
    }
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));
  const addLabel = () => {
    if (newLabel.trim() && !labels.includes(newLabel.trim())) {
      setLabels([...labels, newLabel.trim()]);
      setNewLabel('');
    }
  };
  const removeLabel = (label: string) => setLabels(labels.filter((l) => l !== label));
  const addUserTag = (userId: string) => {
    if (!userTags.includes(userId)) setUserTags([...userTags, userId]);
    setMentionSearch('');
    setShowMentionDropdown(false);
  };
  const removeUserTag = (userId: string) => setUserTags(userTags.filter((u) => u !== userId));

  const filteredUsers = crmUsers.filter(
    (u) => u.displayName?.toLowerCase().includes(mentionSearch.toLowerCase()) && !userTags.includes(u.uid || '')
  );

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const isUploading = files.some((f) => f.status === 'uploading');
  const hasFilesToUpload = pendingCount > 0;

  return (
    <div className="media-uploader-v2">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        multiple={allowMultiple}
        onChange={handleFileSelect}
        className="media-uploader-input-hidden"
      />

      {/* Primary drop zone — always visible */}
      <div
        ref={dropZoneRef}
        className={`media-uploader-dropzone ${isDragging ? 'is-dragging' : ''} ${files.length > 0 ? 'has-selection' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="media-uploader-dropzone-inner">
          <div className="media-uploader-icon-wrap">
            <ImagePlus size={40} strokeWidth={1.5} />
          </div>
          <h4 className="media-uploader-headline">
            {allowMultiple ? 'Select multiple images or files' : 'Select a file'}
          </h4>
          <p className="media-uploader-subline">
            {isDragging
              ? 'Drop files here'
              : 'Drag & drop or click to browse'}
          </p>
          <span className="media-uploader-limit">Max {formatFileSize(maxFileSize)} per file</span>
        </div>
      </div>

      {/* Add more button — when files already selected */}
      {files.length > 0 && allowMultiple && (
        <button
          type="button"
          className="media-uploader-add-more"
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
        >
          <Plus size={18} />
          Add more files
        </button>
      )}

      {/* Selected files grid */}
      {files.length > 0 && (
        <div className="media-uploader-grid">
          <div className="media-uploader-grid-header">
            <span className="media-uploader-count">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="media-uploader-grid-inner">
            {files.map((uploadFile, index) => (
              <div
                key={`${uploadFile.file.name}-${index}`}
                className={`media-uploader-card media-uploader-card--${uploadFile.status}`}
              >
                <div className="media-uploader-card-preview">
                  {uploadFile.preview && uploadFile.file.type.startsWith('video/') ? (
                    <video src={uploadFile.preview} muted playsInline preload="metadata" onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.5; }} />
                  ) : uploadFile.preview ? (
                    <img src={uploadFile.preview} alt={uploadFile.file.name} />
                  ) : (
                    <span className="media-uploader-card-icon">
                      {uploadFile.file.type.startsWith('image/') ? <FileImage size={28} /> : uploadFile.file.type.startsWith('video/') ? <FileVideo size={28} /> : <File size={28} />}
                    </span>
                  )}
                  {uploadFile.status === 'uploading' && (
                    <div className="media-uploader-card-progress">
                      <div className="media-uploader-card-progress-bar" style={{ width: `${uploadFile.progress}%` }} />
                    </div>
                  )}
                  {uploadFile.status === 'complete' && (
                    <div className="media-uploader-card-badge media-uploader-card-badge--success">Done</div>
                  )}
                  {uploadFile.status === 'error' && (
                    <div className="media-uploader-card-badge media-uploader-card-badge--error">Error</div>
                  )}
                </div>
                <div className="media-uploader-card-info">
                  <span className="media-uploader-card-name" title={uploadFile.file.name}>{uploadFile.file.name}</span>
                  <span className="media-uploader-card-meta">{formatFileSize(uploadFile.file.size)}</span>
                  {uploadFile.error && <span className="media-uploader-card-error">{uploadFile.error}</span>}
                </div>
                <button
                  type="button"
                  className="media-uploader-card-remove"
                  onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                  disabled={uploadFile.status === 'uploading'}
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tagging panel — collapsible */}
      {showTagging && files.length > 0 && (
        <div className={`media-uploader-tagging ${showTaggingPanel ? 'is-open' : ''}`}>
          <button
            type="button"
            className="media-uploader-tagging-toggle"
            onClick={() => setShowTaggingPanel(!showTaggingPanel)}
          >
            Apply tags & metadata to all
          </button>
          {showTaggingPanel && (
            <div className="media-uploader-tagging-fields">
              <div className="media-uploader-field">
                <label>Description</label>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description..." />
              </div>
              <div className="media-uploader-field">
                <label>Tags</label>
                <div className="media-uploader-tag-row">
                  {tags.map((tag) => (
                    <span key={tag} className="media-uploader-chip">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}><X size={12} /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                  />
                  <button type="button" className="media-uploader-chip-add" onClick={addTag} disabled={!newTag.trim()}>+</button>
                </div>
              </div>
              <div className="media-uploader-field">
                <label>Labels</label>
                <div className="media-uploader-tag-row">
                  {labels.map((label) => (
                    <span key={label} className="media-uploader-chip media-uploader-chip--label">
                      {label}
                      <button type="button" onClick={() => removeLabel(label)}><X size={12} /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                    placeholder="Add label..."
                  />
                  <button type="button" className="media-uploader-chip-add" onClick={addLabel} disabled={!newLabel.trim()}>+</button>
                </div>
              </div>
              <div className="media-uploader-field">
                <label>Tag users</label>
                <div className="media-uploader-mention-wrap">
                  {userTags.map((userId) => {
                    const u = crmUsers.find((usr) => usr.uid === userId);
                    return (
                      <span key={userId} className="media-uploader-chip media-uploader-chip--user">
                        @{u?.displayName || 'Unknown'}
                        <button type="button" onClick={() => removeUserTag(userId)}><X size={12} /></button>
                      </span>
                    );
                  })}
                  <input
                    type="text"
                    value={mentionSearch}
                    onChange={(e) => { setMentionSearch(e.target.value); setShowMentionDropdown(true); }}
                    onFocus={() => setShowMentionDropdown(true)}
                    placeholder="@mention..."
                  />
                  {showMentionDropdown && mentionSearch && filteredUsers.length > 0 && (
                    <div className="media-uploader-mention-dropdown">
                      {filteredUsers.slice(0, 5).map((u) => (
                        <button key={u.uid} type="button" className="media-uploader-mention-option" onClick={() => u.uid && addUserTag(u.uid)}>
                          {u.displayName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="media-uploader-actions">
        {onCancel && (
          <button type="button" className="media-uploader-btn media-uploader-btn--ghost" onClick={onCancel} disabled={isUploading}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="media-uploader-btn media-uploader-btn--primary"
          onClick={handleUpload}
          disabled={!hasFilesToUpload || isUploading}
        >
          {isUploading ? (
            <>Uploading...</>
          ) : (
            <>
              <Upload size={18} />
              Upload {pendingCount} file{pendingCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MediaUploader;
